import { supabase } from "~/integrations/supabase/client";

import type {
  SupplierBalanceSummary,
  SupplierDocumentAtCutoff,
  SupplierOpenBalance,
} from "~/types/purchasing";

// These RPCs were added after the last supabase types regeneration; call them through a
// thin typed wrapper (same approach as inventoryVarianceService / costingService.rpcCall).
const rpcCall = supabase.rpc.bind(supabase) as unknown as <T>(
  fn: string,
  args?: Record<string, unknown>
) => Promise<{ data: T | null; error: { message: string } | null }>;

/** One open document row parsed from the "Carga de Saldos" (lista_deudas) upload. */
export interface SupplierBalanceImportRow {
  codigo: string;
  ap_account_code: string | null;
  doc_tipo_code: string | null;
  folio: string | null;
  emission_date: string | null; // YYYY-MM-DD
  due_date: string | null; // YYYY-MM-DD
  saldo_clp: number; // negative = payable, positive = credit (NC)
  exchange_rate: number | null;
  is_foreign: boolean;
}

export interface SupplierBalanceImportResult {
  imported: number;
  unmatched: string[]; // códigos with no matching supplier
}

export const supplierBalanceService = {
  /** Open AP balance summary per supplier (saldo total + overdue), for the list. */
  async getSummary(): Promise<SupplierBalanceSummary[]> {
    const { data, error } = await rpcCall<SupplierBalanceSummary[]>(
      "get_supplier_balances_summary"
    );
    if (error) throw new Error(error.message);
    return data ?? [];
  },

  /** A single supplier's open documents with aging (estado de cuenta). */
  async getOpenBalances(supplierId: string): Promise<SupplierOpenBalance[]> {
    const { data, error } = await rpcCall<SupplierOpenBalance[]>(
      "get_supplier_open_balances",
      { p_supplier_id: supplierId }
    );
    if (error) throw new Error(error.message);
    return data ?? [];
  },

  /** Open AP balance summary per supplier AS OF a cutoff date (corte histórico). cutoff = today
   * returns the current balance. */
  async getSummaryAtCutoff(cutoff: string): Promise<SupplierBalanceSummary[]> {
    const { data, error } = await rpcCall<SupplierBalanceSummary[]>(
      "get_supplier_balances_summary_at_cutoff",
      { p_cutoff: cutoff }
    );
    if (error) throw new Error(error.message);
    return data ?? [];
  },

  /** Line-level open documents at a cutoff — feeds the aging reports. Optionally scoped per empresa. */
  async getDocumentsAtCutoff(
    cutoff: string,
    companyId?: string
  ): Promise<SupplierDocumentAtCutoff[]> {
    const { data, error } = await rpcCall<SupplierDocumentAtCutoff[]>(
      "get_supplier_documents_at_cutoff",
      { p_cutoff: cutoff, p_company_id: companyId ?? null }
    );
    if (error) throw new Error(error.message);
    return data ?? [];
  },

  /** Carga de Saldos — replace a company's opening balances for a cutoff. Matches by softland_code. */
  async importBalances(input: {
    cutoffDate: string;
    rows: SupplierBalanceImportRow[];
    companyId: string;
  }): Promise<SupplierBalanceImportResult> {
    const { data, error } = await rpcCall<SupplierBalanceImportResult>(
      "import_supplier_opening_balances",
      {
        p_cutoff_date: input.cutoffDate,
        p_rows: input.rows,
        p_company_id: input.companyId,
      }
    );
    if (error) throw new Error(error.message);
    return data ?? { imported: 0, unmatched: [] };
  },
};
